import { useDispatch, useSelector } from 'react-redux'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import {
  selectBillingSubscriptionData,
  selectCustomerSubscriptionData,
  selectElectricityMeterSubscriptionData,
  selectEnergyCharacteristicsSubscriptionData,
  selectEnergyContractSubscriptionData,
  selectGasMeterSubscriptionData,
  selectMainAddressSubscriptionData,
  selectTelecomContractSubscriptionData,
} from '../SubscriptionFormSelectors'
import { selectPageConfigurationData } from '@/store/PageConfiguration/PageConfigurationSelectors'
import {
  selectBankingModuleConfigurationData,
  selectCommonFormConfigurationData,
  selectCustomerModuleConfigurationData,
  selectProviderConfiguration,
} from '../SubscriptionFormConfigurationSelectors'
import { updateAdditionalSalesData, updateCustomerData } from '../SubscriptionFormSlice'
import {
  addCommonFormErrors,
  deleteCommonFormErrors,
  updateCustomerModuleConfiguration,
} from '@/containers/SubscriptionFormPage/SubscriptionFormConfigurationSlice'
import { subscriptionFormActions } from '@/containers/SubscriptionFormPage/SubscriptionFormSagas'
import useBusinessType from '@/shared/hooks/useBusinessType'
import { t } from 'i18next'

const useAdditionalSaleFormData = () => {
  const dispatch = useDispatch()
  const {
    zohoId,
    name,
    surname,
    civility,
    phone1,
    phone2,
    email,
    birthdate,
    pro_social_reason: proSocialReason,
    pro_siret: proSiret,
  } = useSelector(selectCustomerSubscriptionData)

  const { iban, bic } = useSelector(selectBillingSubscriptionData)
  const { isMcpAgent } = useSelector(selectAuthData)
  const { streetNumber, city, zipcode, address, inseeCode, housingType, tenantType, floor } = useSelector(
    selectMainAddressSubscriptionData,
  )
  const { pceNo: pce } = useSelector(selectGasMeterSubscriptionData)
  const { pdlNo: pdl } = useSelector(selectElectricityMeterSubscriptionData)
  const { siretIsLoading, siretIsValid } = useSelector(selectCustomerModuleConfigurationData)
  const { prestationType } = useSelector(selectEnergyCharacteristicsSubscriptionData)

  const { providerId } = useSelector(selectTelecomContractSubscriptionData)
  const { name: providerName } = useSelector(selectProviderConfiguration)
  const { offerId } = useSelector(selectEnergyContractSubscriptionData)

  const { type: businessType } = useBusinessType({
    loaderRoutesEnergy: {},
    loaderRoutesInsurance: {},
    loaderRoutes: {},
    modulesStatuses: {},
    modulesStatusesEnergy: {},
    modulesStatusesInsurance: {},
  })

  const removeErrors = error => {
    dispatch(deleteCommonFormErrors(error))
  }

  const customerUpdate = payload => {
    dispatch(updateCustomerData(payload))
  }

  const handleSiretResponse = response => {
    if (!response.valid) {
      dispatch(
        addCommonFormErrors({
          siret: [t('subscriptionForm:customer_module.proSiret.error')],
        }),
      )
      customerUpdate({
        pro_ape_code: '',
        pro_social_reason: '',
      })
      dispatch(updateCustomerModuleConfiguration({ siretIsValid: false }))
      return
    }

    customerUpdate({
      pro_ape_code: response.apeCode,
      pro_social_reason: response.socialReason,
    })
    dispatch(updateCustomerModuleConfiguration({ siretIsValid: true }))
  }

  const verifySiret = () => {
    dispatch({
      type: subscriptionFormActions.CUSTOMER_MODULE_VERIFY_SIRET,
      siret: proSiret,
      callback: handleSiretResponse,
    })
  }

  const {
    verify_iban_url: verifyIbanUrl,
    current_subscriber: subscription,
    assistanceProducts: assistanceList,
    axa_email_url: axaEmailUrl,
    additional_sales_url: saveLeadUrl,
    telecomEnergy: telecomEnergyList,
    protected: protectedList,
    protectedClubProducts: protectedClubList,
    carbonOffsetProducts: carbonOffsetList,
    assuranceProducts: assuranceList,
    telecomProducts: telecomList,
    floraAssurance: floraAssuranceList,
    yagoAssurance: yagoAssuranceList,
    santevetAssurance: santevetAssuranceList,
    ethias: ethiasList,
    eliteSaleProduct: eliteList,
    verisureTelecomBe: verisureTelecomBeList,
    boilerCoverEneco: boilerCoverEnecoList,
    boilerCoverEngie: boilerCoverEngieList,
    boilerCoverHomeserve: boilerCoverHomeserveList,
    additionalSalesStatuses,
    country,
    yoloProducts: yoloList,
    irenProducts: irenList,
    timProducts: timList,
    fastWebProducts: fastWebList,
    protectedItProducts: protectedItList,
    edisonProducts: edisonList,
    enelProducts: enelList,
    sorgeniaProducts: sorgeniaList,
  } = useSelector(selectPageConfigurationData)

  const { ibanValidationData } = useSelector(selectBankingModuleConfigurationData)

  const isAlreadyMonConseillerPerso =
    !!subscription?.sale_mon_conseiller_perso && subscription.sale_mon_conseiller_perso.status_code !== 'abandon'
  const { token, isEditable, mandatoryFields, errors } = useSelector(selectCommonFormConfigurationData)
  const agentEmail = subscription?.created_by?.email ?? ''

  const updateLead = (data, name) => {
    dispatch(updateAdditionalSalesData({ data, name }))
  }

  return {
    zohoId,
    name,
    surname,
    civility,
    phone1,
    phone2,
    email,
    birthdate,
    proSocialReason,
    proSiret,
    pdl,
    pce,
    prestationType,
    iban,
    bic,
    streetNumber,
    city,
    zipcode,
    address,
    inseeCode,
    agentEmail,
    housingType,
    tenantType,
    providerId,
    providerName,
    offerId,
    country,
    token,
    isEditable,
    ibanValidationData,
    businessType,
    mandatoryFields,
    errors,
    removeErrors,
    additionalSalesStatuses,
    siretIsLoading,
    siretIsValid,
    verifySiret,
    updateLead,
    floor,
    urls: {
      verifyIban: verifyIbanUrl,
      saveLeadUrl,
    },
    showOnlyAllowLeadMcp: !isMcpAgent && isAlreadyMonConseillerPerso,
    haveAlreadyAnyLead:
      !!subscription?.sale_telecom_energy ||
      !!subscription?.sale_telecom ||
      !!subscription?.sale_verisure_telecom ||
      !!subscription?.sale_assurance_default ||
      !!subscription?.sale_assurance_emprunteur,
    sales: {
      leadAssistance: {
        list: assistanceList ?? [],
        saleDatabaseData: subscription?.sale_assistance_id,
        axaEmailUrl,
      },
      leadTelecomEnergy: {
        list: telecomEnergyList ?? [],
        saleDatabaseData: subscription?.sale_telecom_energy,
      },
      leadProtected: {
        list: protectedList ?? [],
        saleDatabaseData: subscription?.sale_protected,
      },
      leadProtectedClub: {
        list: protectedClubList ?? [],
        saleDatabaseData: subscription?.sale_protected_club,
      },
      leadCarbonOffset: {
        list: carbonOffsetList ?? [],
        saleDatabaseData: subscription?.sale_carbon_offset,
      },
      leadAssurance: {
        list: assuranceList ?? [],
        saleDatabaseData: subscription?.sale_assurance,
      },
      leadTelecom: {
        list: telecomList ?? [],
        saleDatabaseData: subscription?.sale_telecom,
      },
      leadFloraAssuranceEthias: {
        list: floraAssuranceList ?? [],
        saleDatabaseData: subscription?.sale_flora_assurance_ethias,
      },
      leadFloraAssuranceLead: {
        list: floraAssuranceList ?? [],
        saleDatabaseData: subscription?.sale_flora_assurance_lead,
      },
      leadYagoAssuranceHouse: {
        list: yagoAssuranceList ?? [],
        saleDatabaseData: subscription?.sale_yago_assurance_house,
      },
      leadYagoAssuranceCar: {
        list: yagoAssuranceList ?? [],
        saleDatabaseData: subscription?.sale_yago_assurance_car,
      },
      leadYagoAssuranceMoto: {
        list: yagoAssuranceList ?? [],
        saleDatabaseData: subscription?.sale_yago_assurance_moto,
      },
      leadSantevetAssurance: {
        list: santevetAssuranceList ?? [],
        saleDatabaseData: subscription?.sale_sante_vet_assurance,
      },
      leadEthias: {
        list: ethiasList ?? [],
        saleDatabaseData: subscription?.sale_ethias,
      },
      leadElite: {
        list: eliteList ?? [],
        saleDatabaseData: subscription?.sale_elite,
      },
      leadVerisureTelecomBe: {
        list: verisureTelecomBeList ?? [],
        saleDatabaseData: subscription?.sale_verisure_be,
      },
      leadBoilerEneco: {
        list: boilerCoverEnecoList ?? [],
        saleDatabaseData: subscription?.sale_boiler_eneco,
      },
      leadBoilerEngie: {
        list: boilerCoverEngieList ?? [],
        saleDatabaseData: subscription?.sale_boiler_engie,
      },
      leadBoilerHomeserve: {
        list: boilerCoverHomeserveList ?? [],
        saleDatabaseData: subscription?.sale_boiler_homeserve,
      },
      leadYolo: {
        list: yoloList ?? [],
        saleDatabaseData: subscription?.sale_yo_casa,
      },
      leadIren: {
        list: irenList ?? [],
        saleDatabaseData: subscription?.sale_ir,
      },
      leadTim: {
        list: timList ?? [],
        saleDatabaseData: subscription?.sale_ti_tim,
      },
      leadFastWeb: {
        list: fastWebList ?? [],
        saleDatabaseData: subscription?.sale_fw,
      },
      leadProtectedIt: {
        list: protectedItList ?? [],
        saleDatabaseData: subscription?.sale_protected_it,
      },
      leadEdison: {
        list: edisonList ?? [],
        saleDatabaseData: subscription?.sale_edison,
      },
      leadEnel: {
        list: enelList ?? [],
        saleDatabaseData: subscription?.sale_enel,
      },
      leadSorgenia: {
        list: sorgeniaList ?? [],
        saleDatabaseData: subscription?.sale_sorgenia,
      },
    },
  }
}

export default useAdditionalSaleFormData
